import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { AppIcon } from '../app-icon/app-icon';

interface FileIconMetadata {
  readonly icon: string;
  readonly alt: string;
}

@Component({
  selector: 'app-icon-file',
  imports: [AppIcon],
  templateUrl: './app-icon-file.html',
  styleUrl: './app-icon-file.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppIconFile {
  readonly fileName = input.required<string>();
  readonly size = input(24);

  protected readonly fileIcon = computed<FileIconMetadata>(() => {
    switch (this.fileExtension()) {
      case 'pdf':
        return { icon: 'icon-pdf-color.svg', alt: 'PDF file' };
      case 'doc':
      case 'docx':
      case 'dot':
      case 'dotx':
        return { icon: 'icon-brand-word-color.svg', alt: 'Word file' };
      case 'xls':
      case 'xlsx':
      case 'xlsm':
      case 'csv':
        return { icon: 'icon-brand-excel-color.svg', alt: 'Excel file' };
      case 'ppt':
      case 'pptx':
      case 'pps':
      case 'ppsx':
        return { icon: 'icon-brand-powerpoint-color.svg', alt: 'PowerPoint file' };
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return { icon: 'icon-zipfile-color.svg', alt: 'Archive file' };
      case 'wav':
        return { icon: 'icon-wav-color.svg', alt: 'WAV file' };
      case 'mp3':
      case 'm4a':
      case 'aac':
      case 'flac':
      case 'ogg':
        return { icon: 'icon-audio-color.svg', alt: 'Audio file' };
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'mkv':
      case 'webm':
        return { icon: 'icon-video-color.svg', alt: 'Video file' };
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'svg':
      case 'bmp':
      case 'tif':
      case 'tiff':
        return { icon: 'icon-image-color.svg', alt: 'Image file' };
      case 'eml':
      case 'msg':
        return { icon: 'icon-email-color.svg', alt: 'Email file' };
      case 'html':
      case 'css':
      case 'scss':
      case 'js':
      case 'json':
      case 'ts':
      case 'xml':
      case 'yaml':
      case 'yml':
        return { icon: 'icon-code-color-blue.svg', alt: 'Code file' };
      case 'txt':
      case 'rtf':
      case 'md':
        return { icon: 'icon-text-regular.svg', alt: 'Text file' };
      default:
        return { icon: 'icon-file-edit-regular.svg', alt: 'File' };
    }
  });

  private readonly fileExtension = computed(() => {
    const cleanFileName = this.fileName().split(/[?#]/, 1)[0].trim();
    const extensionStart = cleanFileName.lastIndexOf('.');

    if (extensionStart < 0 || extensionStart === cleanFileName.length - 1) {
      return '';
    }

    return cleanFileName.slice(extensionStart + 1).toLowerCase();
  });
}
